/**
 * BeHeard menu configuration for the suite shell.
 *
 * Each item has a visibility gate:
 *
 *   - `requireScope`  — item shows only if this scope is present in
 *     the effective workspace scopes (injected by BootGate, including
 *     Testing Mode simulated scopes). Owner/admin bypass all gates.
 *
 *   - `viewerVisible`  — item shows for viewer-role users (read-only).
 *
 *   - Items with neither gate show for every authenticated user.
 *
 * Authorization truth remains with hasCapability / requireCapability.
 * This config controls NAVIGATION VISIBILITY only — clicking a visible
 * link still hits a CapabilityGuard route-level check and/or backend
 * requireCapability middleware.
 *
 * Scope keys come from shared/src/appRoles.ts SCOPE_BUNDLES.
 */

import type { ReactNode } from 'react';
import { createElement } from 'react';
import type { ServerCapabilities } from '../store/api/capabilitiesApi';
import {
  Home,
  Mic,
  ClipboardCheck,
  ShoppingBag,
  User,
  MessageSquare,
  GraduationCap,
  Flower2,
  Layers,
  TrendingUp,
  Briefcase,
  Clock,
  Trophy,
  Award,
  Users as UsersIcon,
  ClipboardList,
  Headphones,
  HandCoins,
  FolderOpen,
  Wallet,
  Gift,
  KeyRound,
  UserCheck,
  Languages,
  Target,
  Music,
  Handshake,
  ShieldCheck,
} from 'lucide-react';

// ============================================================
// Menu route mapping (MODULE-LEVEL CONSTANT — required by adapter)
// ============================================================

export const MENU_ROUTES: Record<string, string> = {
  home: '/dashboard',
  practice: '/practice',
  interview_prep: '/interview-prep',
  language_learning: '/language-learning',
  test: '/skills',
  marketplace: '/marketplace',
  profile: '/profile',
  coaching: '/coaching',
  learn: '/learn',
  petals: '/petals',
  blueprints: '/blueprints',
  progress: '/progress',
  portfolio: '/portfolio',
  timeline: '/my/timeline',
  leaderboard: '/leaderboard',
  certifications: '/certifications',
  recruiter: '/recruiter',
  training: '/training',
  my_training: '/my-training',
  coach_dashboard: '/coach/dashboard',
  hire_requests: '/marketplace/hire-requests',
  content: '/content',
  usage: '/usage',
  referrals: '/referrals',
  capabilities: '/my/capabilities',
  challenges: '/challenges',
  song_learning: '/song-learning',
  my_coach: '/coaching/my-coach',
  admin: '/admin',
};

// ============================================================
// Menu icon mapping
// ============================================================

const MENU_ICONS: Record<string, typeof Home> = {
  home: Home,
  practice: Mic,
  interview_prep: UserCheck,
  language_learning: Languages,
  test: ClipboardCheck,
  marketplace: ShoppingBag,
  profile: User,
  coaching: MessageSquare,
  learn: GraduationCap,
  petals: Flower2,
  blueprints: Layers,
  progress: TrendingUp,
  portfolio: Briefcase,
  timeline: Clock,
  leaderboard: Trophy,
  certifications: Award,
  recruiter: UsersIcon,
  training: ClipboardList,
  my_training: ClipboardList,
  coach_dashboard: Headphones,
  hire_requests: HandCoins,
  content: FolderOpen,
  usage: Wallet,
  referrals: Gift,
  capabilities: KeyRound,
  challenges: Target,
  song_learning: Music,
  my_coach: Handshake,
  admin: ShieldCheck,
};

export function getMenuIcon(menuKey: string, className?: string): ReactNode {
  const Icon = MENU_ICONS[menuKey];
  if (!Icon) return null;
  return createElement(Icon, { className: className ?? 'w-5 h-5', size: 20 });
}

// ============================================================
// Static menu data
//
// Visibility logic (evaluated by useStaticMenuQuery):
//
//   owner / admin → see EVERYTHING (bypass all gates)
//
//   member → see items where:
//     a) no requireScope (always visible to any member), OR
//     b) requireScope is present in workspace.scopes
//
//   viewer → see items where viewerVisible === true
//
// Scopes come from SCOPE_BUNDLES in shared/src/appRoles.ts and are
// injected into the workspace by BootGate (including Testing Mode).
// ============================================================

export interface BeheardMenuItem {
  menuKey: string;
  label: string;
  order: number;
  /** Scope that must be present in workspace.scopes for members. */
  requireScope?: string;
  /** If true, visible to viewer-role users (read-only). Default: false. */
  viewerVisible?: boolean;
  /**
   * Server feature switch (GET /capabilities) that must be ON for the item to
   * show — for EVERY role, owner/admin included: a switched-off feature has no
   * page to open. Hidden while the capabilities document is unknown.
   */
  requireServerCapability?: ServerFeatureSwitch;
  /**
   * Shown ONLY when the server reports at least one admin function for the
   * caller (GET /capabilities → admin.functions), for every role — owner/admin
   * included: a workspace role label is not admin authority. Hidden while the
   * capabilities document is unknown.
   */
  requireAdminFunction?: true;
}

/**
 * Boolean feature switches reported by GET /capabilities. `conversationEngine`
 * is the nested `conversation.engine` (mapped by menuServerSwitches).
 */
export type ServerFeatureSwitch = 'teamChallenges' | 'songLearning' | 'rag' | 'conversationEngine';

/**
 * A feature the SERVER has switched off has no page to open, whoever the
 * caller is — so this applies before the owner/admin bypass. Unknown (the
 * capabilities document not loaded, or failed) hides the item.
 */
export function serverSwitchAllows(
  item: BeheardMenuItem,
  caps: Partial<Record<ServerFeatureSwitch, boolean>> | undefined,
): boolean {
  if (!item.requireServerCapability) return true;
  return caps?.[item.requireServerCapability] === true;
}

/**
 * The menu's server switches from the capabilities document. The conversation
 * engine is nested (`conversation.engine`); anything not reported as `true` —
 * including an unknown or failed document — leaves the switch off (hidden).
 */
export function menuServerSwitches(
  caps: ServerCapabilities | undefined,
): (Partial<Record<ServerFeatureSwitch, boolean>> & { admin?: ServerCapabilities['admin'] }) | undefined {
  if (!caps) return undefined;
  return { ...caps, conversationEngine: caps.conversation?.engine === true };
}

/**
 * The Admin entry is capability-driven: visible only when the server lists an
 * admin function for this caller. Applies before the owner/admin bypass.
 */
export function adminEntryAllows(
  item: BeheardMenuItem,
  caps: { admin?: { functions: unknown[] } } | undefined,
): boolean {
  if (!item.requireAdminFunction) return true;
  return (caps?.admin?.functions?.length ?? 0) > 0;
}

export const STATIC_MENU: BeheardMenuItem[] = [
  // ── CORE (always visible, including viewer) ──
  { menuKey: 'home',    label: 'Home',    order: 10, viewerVisible: true },
  { menuKey: 'profile', label: 'Profile', order: 50, viewerVisible: true },

  // ── STANDARD MEMBER (require practice/coaching/marketplace scopes) ──
  { menuKey: 'practice',    label: 'Practice',    order: 20, requireScope: 'beheard:practice:read' },
  // Interview Prep runs on the coaching conversation runtime, so it needs coaching read
  // and the server's conversation engine (its catalog is 'Not available' while the engine is off).
  { menuKey: 'interview_prep', label: 'Interview Prep', requireServerCapability: 'conversationEngine', order: 25, requireScope: 'beheard:coaching:read' },
  // Language Learning runs on the same coaching conversation runtime.
  { menuKey: 'language_learning', label: 'Language Learning', requireServerCapability: 'conversationEngine', order: 26, requireScope: 'beheard:coaching:read' },
  { menuKey: 'test',        label: 'Skill practice', order: 30, requireScope: 'beheard:practice:read' },
  { menuKey: 'marketplace', label: 'Marketplace', order: 40, requireScope: 'beheard:marketplace:read' },
  { menuKey: 'coaching',    label: 'Coaching',    order: 110, requireScope: 'beheard:coaching:read' },
  { menuKey: 'learn',       label: 'Learn',       order: 120, requireScope: 'beheard:practice:read' },
  { menuKey: 'petals',      label: 'Story Prep',  order: 130, requireScope: 'beheard:practice:write' },
  // 'events' (Events Prep) retired: its backend was a stub that persisted nothing (RC10).
  { menuKey: 'blueprints',  label: 'Blueprints',  order: 150, requireScope: 'beheard:practice:write' },
  // Song learning (P5.5) runs on the coaching conversation runtime; optional, OFF by default.
  { menuKey: 'song_learning', label: 'Song Learning', order: 160, requireScope: 'beheard:coaching:read', requireServerCapability: 'songLearning' },
  // Human coaching, client side: engagements and bookings made from Marketplace → Experts.
  { menuKey: 'my_coach',    label: 'My Coach',    order: 115, requireScope: 'beheard:coaching:read' },

  // ── PROGRESS (require practice read — track your own progress) ──
  { menuKey: 'progress',       label: 'Progress',       order: 210, requireScope: 'beheard:practice:read' },
  { menuKey: 'portfolio',      label: 'Portfolio',      order: 220, requireScope: 'beheard:portfolio:read' },
  { menuKey: 'timeline',       label: 'Timeline',       order: 230, requireScope: 'beheard:practice:read' },
  { menuKey: 'leaderboard',    label: 'Leaderboard',    order: 240, requireScope: 'beheard:practice:read' },
  { menuKey: 'certifications', label: 'Certifications', order: 250, requireScope: 'beheard:practice:read' },

  // ── MANAGEMENT (require specific bundle scopes) ──
  { menuKey: 'recruiter',       label: 'Recruiter',       order: 310, requireScope: 'beheard:recruiter:read' },
  { menuKey: 'training',        label: 'Training',        order: 320, requireScope: 'beheard:training:write' },
  { menuKey: 'my_training',     label: 'My Training',     order: 325, requireScope: 'beheard:training:read' },
  // Team challenges (P3.5): the route needs training:read; the server switch must be on.
  { menuKey: 'challenges',      label: 'Team Challenges', order: 327, requireScope: 'beheard:training:read', requireServerCapability: 'teamChallenges' },
  { menuKey: 'coach_dashboard', label: 'Coach Dashboard', order: 330, requireScope: 'beheard:coaching:write' },
  { menuKey: 'hire_requests',   label: 'Hire Requests',   order: 340, requireScope: 'beheard:recruiter:read' },
  // Hidden while the server has no real semantic retrieval (GET /capabilities rag: false).
  { menuKey: 'content',         label: 'Content Library', order: 350, requireScope: 'beheard:training:read', requireServerCapability: 'rag' },

  // ── UTILITY (require billing scope or always visible for members) ──
  { menuKey: 'usage',        label: 'Usage & Limits', order: 410, requireScope: 'beheard:billing:read' },
  { menuKey: 'referrals',    label: 'Referrals',       order: 420 },
  { menuKey: 'capabilities', label: 'My Capabilities', order: 430 },

  // ── ADMIN (server-reported admin functions only; see adminEntryAllows) ──
  { menuKey: 'admin', label: 'Admin', order: 900, requireAdminFunction: true },
];
